import { Boxes, FileSignature, Globe, Mail, Phone, User } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { LucideIcon } from 'lucide-react'
import { fmtMoney } from '../../../shared/format'
import { useCustomerModules, useOffers } from '../../api'
import type { Customer } from '../../types'
import { maskPhone } from '../../ui'
import { ModuleStatusBadge, OfferStatusBadge } from '../CrmBits'
import { LiveLinks } from '../LiveLinks'

function Card({ title, icon: Icon, children }: { title: string; icon: LucideIcon; children: ReactNode }) {
  return (
    <section className="rounded-xl border bg-card p-4 shadow-card">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <Icon className="size-4 text-muted-foreground" aria-hidden />
        {title}
      </h2>
      {children}
    </section>
  )
}

/** Toplantıda göz ucuyla bakılacak müşteri bilgileri. */
export function LiveSidebar({ customer: c }: { customer: Customer }) {
  const { t } = useTranslation()
  const modules = (useCustomerModules().data ?? []).filter((m) => m.customerId === c.id && m.status !== 'cancelled')
  const offers = (useOffers(c.id).data ?? []).filter((o) => o.status === 'draft' || o.status === 'sent')

  return (
    <div className="space-y-4">
      <Card title={t('crm.overview.contact')} icon={User}>
        <p className="font-medium">{c.contactName}</p>
        {c.contactTitle ? <p className="text-xs text-muted-foreground">{c.contactTitle}</p> : null}
        <div className="mt-2 space-y-1.5 text-sm">
          {c.phone ? (
            <a href={`tel:${c.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-primary">
              <Phone className="size-4 text-muted-foreground" aria-hidden />
              {maskPhone(c.phone)}
            </a>
          ) : null}
          {c.email ? (
            <a href={`mailto:${c.email}`} className="flex min-w-0 items-center gap-2 text-primary hover:underline">
              <Mail className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              <span className="truncate">{c.email}</span>
            </a>
          ) : null}
        </div>
      </Card>

      <Card title={t('crm.project.modules')} icon={Boxes}>
        {modules.length ? (
          <ul className="space-y-2">
            {modules.map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="min-w-0 truncate">{m.name}</span>
                <ModuleStatusBadge status={m.status} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-muted-foreground">{t('crm.module.empty')}</p>
        )}
      </Card>

      <Card title={t('crm.live.openOffers')} icon={FileSignature}>
        {offers.length ? (
          <ul className="space-y-2">
            {offers.map((o) => (
              <li key={o.id} className="flex items-center gap-2 text-sm">
                <span className="min-w-0 flex-1 truncate font-mono text-xs">{o.number}</span>
                <span className="font-semibold tabular-nums">{fmtMoney(o.total)}</span>
                <OfferStatusBadge status={o.status} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-muted-foreground">{t('crm.live.noOpenOffers')}</p>
        )}
      </Card>

      <Card title={t('crm.links.title')} icon={Globe}>
        <LiveLinks customer={c} canWrite={false} />
      </Card>
    </div>
  )
}
